import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import { FormBuilder, FormGroup } from "@angular/forms";
import { SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import {DialogService, SharedUiDialogModule} from '@snelstart/ui-dialog';
import { CommonModule } from '@angular/common';
import { BokkzAlgDataService} from "../services/bokkz-alg-data-service";
import {
  BokkzMumaClient, BokkzMumaDataService,
  MumaEntity,
  MumaEntityMachtigingStatus, MumaRequest,
  MumaRequestDienst, MumaRequestMutatieType,
} from "@snelstart-ventures/fiscaal/domain-logic";
import { of, Subject, takeUntil } from "rxjs";
import { SharedUiTableModule, TableConfiguration, ZeroStateConfiguration} from "@snelstart/ui-table";
import { SearchCriteria, SearchResult} from "@snelstart/domain-logic";
import { SharedUiBadgeModule} from "@snelstart/ui-badge";

@Component({
  selector: 'snelstart-venture-fiscaal-domain-logic-muma',
  templateUrl: './muma.component.html',
  imports: [
    CommonModule,
    SharedUiButtonModule,
    SharedUiDialogModule,
    SharedUiFormControlsModule,
    SharedUiTableModule,
    SharedUiBadgeModule
  ],
  styleUrl: './muma.component.css'
})
export class MumaComponent implements OnInit, OnChanges, OnDestroy {

  @Input() public dialogVisible = false;
  @Input() public name= {} as string;
  @Input() public mumaList = [] as MumaEntity[];
  @Output() public dialogVisibleChange = new EventEmitter<boolean>();
  protected readonly MumaEntityMachtigingStatus = MumaEntityMachtigingStatus;
  protected readonly MumaRequestMutatieType = MumaRequestMutatieType;
  protected tableConfig!: TableConfiguration<SearchCriteria, MumaEntity>;
  private destroy$ = new Subject<void>();
  public formGroup= {} as FormGroup;
  public mumaEntity = {} as MumaEntity;
  public year = {} as number;
  public error: any;
  public sstRelationId: any;
  public showTable = false;

  constructor(
    private algDataService: BokkzAlgDataService,
    private mumaDataService: BokkzMumaDataService,
    private bokkzMumaClient: BokkzMumaClient,
    private dialogService: DialogService,
    public fb: FormBuilder,
  ) {
    this.formGroup = this.fb.group({
      activatieToken: null as unknown as string,
      id: null as unknown as number,
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dialogVisible']?.currentValue === true) {
      this.createTableConfig();
      this.showTable = true;
    }
  }

  ngOnInit(): void {
    this.algDataService.selectedYear
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => this.year = value);
    this.createTableConfig();
    this.algDataService.sstRelationId
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => this.sstRelationId = value);
    this.createTableConfig();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public createTableConfig(): void {
    this.tableConfig = TableConfiguration.create<SearchCriteria, MumaEntity>({
      identifier: 'id',
      options: {
        useSearch: false,
        useSort: true,
      },
      criteria: {take: 6, skip: 2, sortDescending: false} as SearchCriteria,
      search: () => {
        const sorted = [...this.mumaList].sort((a, b) =>
          this.getTypeName(a.type!).localeCompare(this.getTypeName(b.type!))
        );
        return of(new SearchResult(sorted, sorted.length));
      },
      details: (entity) => {
        if (entity.id != undefined) {
          this.mumaDataService.updateMumaEntity(entity);
        }
      },
      zeroStateConfig: ZeroStateConfiguration.create({
        icon: 'book-open-cover',
        mainText: $localize`Geen machtigingen aanwezig`,
        subText: 'Open het klantdossier en ga naar de module Machtigingen om een nieuwe machtiging aan te maken.',
      }),
    });
  }

  checkActie(mumaEntity : MumaEntity): string {
    if (mumaEntity.machtigingStatus === MumaEntityMachtigingStatus.UNKNOWN
      || mumaEntity.machtigingStatus === MumaEntityMachtigingStatus.AFGEKEURD
      || mumaEntity.machtigingStatus === MumaEntityMachtigingStatus.NIET_ACTIEF) {
      return 'opvoeren'
    }
    else if (mumaEntity.machtigingStatus === MumaEntityMachtigingStatus.IN_BEHANDELING && this.checkIsOptInMachtiging(mumaEntity.mumaRequest?.dienst)) {
      return 'activeren'
    }
    else if (mumaEntity.machtigingStatus === MumaEntityMachtigingStatus.ACTIEF) {
      return 'intrekken'
    }
    else if (this.mumaEntity.mumaRequest?.dienst === MumaRequestDienst.DoMa_IH
      || this.mumaEntity.mumaRequest?.dienst === MumaRequestDienst.VIA_2024
      || this.mumaEntity.mumaRequest?.dienst === MumaRequestDienst.VIA_2025) {
      if (this.mumaEntity.machtigingStatus !== MumaEntityMachtigingStatus.ACTIEF) {
        return 'via ophalen'
      }
      else return '';
    }
    else if (mumaEntity.machtigingStatus === MumaEntityMachtigingStatus.CONTROLEREN) {
      return 'controleren'
    }
    else if (mumaEntity.machtigingStatus === MumaEntityMachtigingStatus.ONTVANGEN) {
      return 'in behandeling'
    }
    else return '';
  }

  getTypeName(type: string): string {
    const typeNameMap: Record<string, string> = {
      'SBA_VpB_2024': 'Machtiging servicebericht aanslag vennootschapsbelasting 2024',
      'SBA_VpB_2025': 'Machtiging servicebericht aanslag vennootschapsbelasting 2025',
      'SBA_VpB_2026': 'Machtiging servicebericht aanslag vennootschapsbelasting 2026',
      'SBA_IH_2024': 'Machtiging servicebericht aanslag inkomstenbelasting 2024',
      'SBA_IH_2025': 'Machtiging servicebericht aanslag inkomstenbelasting 2025',
      'SBA_IH_2026': 'Machtiging servicebericht aanslag inkomstenbelasting 2026',
      'SBA_OB_2024': 'Machtiging servicebericht aanslag omzetbelasting 2024',
      'SBA_OB_2025': 'Machtiging servicebericht aanslag omzetbelasting 2025',
      'SBA_OB_2026': 'Machtiging servicebericht aanslag omzetbelasting 2026',
      'SBA_TSG_2024': 'Machtiging servicebericht toeslagen 2024',
      'SBA_TSG_2025': 'Machtiging servicebericht toeslagen 2025',
      'SBA_TSG_2026': 'Machtiging servicebericht toeslagen 2026',
      'VIA_2024': 'Machtiging gegevens vooraf ingevulde aangifte 2024',
      'VIA_2025': 'Machtiging gegevens vooraf ingevulde aangifte 2025',
      'VIA_2026': 'Machtiging gegevens vooraf ingevulde aangifte 2026',
      'DoMa_IH': 'Doorlopende machtiging inkomstenbelasting',
      'DoMa_OB': 'Doorlopende machtiging omzetbelasting',
      'DoMa_VpB': 'Doorlopende machtiging vennootschapsbelasting',
      'DoMa_TSG': 'Doorlopende machtiging toeslagen',
    };
    return typeNameMap[type]; // Geef de naam terug, of het type zelf als de naam niet gevonden is
  }

  public checkIsOptInMachtiging(dienst?: MumaRequestDienst) {
    return dienst?.toString().includes('SBA_TSG')
      || dienst?.toString().includes('VIA')
      || dienst?.toString().includes('DoMa')
  }

  public showActivatieCode(mumaEntity: MumaEntity): boolean {
    this.formGroup.reset();
    this.formGroup.patchValue({id: mumaEntity.id})
    return true;
  }

  public postEntity(mumaEntity: MumaEntity, mutatieType: MumaRequestMutatieType) {
    let bsn = mumaEntity.taxPayerFiscalNumber;
    if (bsn.includes("B")) {
      bsn = bsn.split("B")[0];
    }
    mumaEntity.mumaRequest = {
      bsn: bsn,
      dienst: mumaEntity.mumaRequest?.dienst,
      activatieToken: this.formGroup.get('activatieToken')?.getRawValue(),
      mutatieType: mutatieType,
      gemachtigdeIdentiteitsNummer: mumaEntity.mumaRequest?.gemachtigdeIdentiteitsNummer,
    } as MumaRequest;
    mumaEntity.machtigingStatus = MumaEntityMachtigingStatus.CONTROLEREN
    this.bokkzMumaClient.postOneMuma(this.sstRelationId, true, mumaEntity).subscribe({
      next: () => {
        this.mumaDataService.updateMumaEntity(mumaEntity);
      },
      complete: () => {
        this.checkActie(mumaEntity);
        this.formGroup.reset();
      }
    })
  }

}
